/**
 * Utilitários para tratamento padronizado de datas no frontend
 */

const BRAZIL_TIMEZONE = 'America/Sao_Paulo';

/**
 * Obtém a data atual no fuso horário brasileiro
 */
export const getBrazilNow = (): Date => {
  return new Date();
};

/**
 * Formata uma data para o padrão brasileiro (dd/MM/yyyy)
 */
export const formatDateBrazil = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: BRAZIL_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(dateObj);
};

/**
 * Alias para formatDateBrazil para compatibilidade
 */
export const formatDate = formatDateBrazil;

/**
 * Formata uma data/hora para o padrão brasileiro (dd/MM/yyyy HH:mm)
 */
export const formatDateTimeBrazil = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: BRAZIL_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(dateObj);
};

/**
 * Alias para formatDateTimeBrazil para compatibilidade
 */
export const formatDateTime = formatDateTimeBrazil;

/**
 * Converte uma data para o formato ISO compatível com input[type="date"]
 * Considera o fuso horário brasileiro
 */
export const toDateInputValue = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Usar Intl.DateTimeFormat para obter a data no fuso horário brasileiro
  const formatter = new Intl.DateTimeFormat('sv-SE', { 
    timeZone: BRAZIL_TIMEZONE 
  });
  
  return formatter.format(dateObj);
};

/**
 * Converte valor de input[type="date"] para DateTimeOffset no fuso brasileiro
 * Retorna string ISO 8601 com timezone offset
 */
export const fromDateInputValue = (value: string): string => {
  if (!value) return '';
  
  // value está no formato "YYYY-MM-DD"
  const [year, month, day] = value.split('-').map(Number);
  
  // Criar data no fuso horário brasileiro
  const date = new Date();
  date.setFullYear(year, month - 1, day); // month é 0-based
  date.setHours(0, 0, 0, 0);
  
  // Obter o offset do fuso brasileiro para esta data
  const brazilOffset = getBrazilTimezoneOffset(date);
  
  // Retornar ISO string com offset
  const isoString = `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}T00:00:00${brazilOffset}`;
  
  return isoString;
};

/**
 * Converte ISO 8601 para valor de input[type="datetime-local"] (YYYY-MM-DDTHH:mm) no fuso brasileiro
 */
export const toDateTimeInputValue = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const formatter = new Intl.DateTimeFormat('sv-SE', {
    timeZone: BRAZIL_TIMEZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
  return formatter.format(dateObj).replace(' ', 'T');
};

/**
 * Converte valor de input[type="datetime-local"] (YYYY-MM-DDTHH:mm) para ISO 8601 com fuso brasileiro
 */
export const fromDateTimeInputValue = (value: string): string => {
  if (!value) return '';
  const [datePart, timePart] = value.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = (timePart ?? '00:00').split(':').map(Number);
  const ref = new Date(year, month - 1, day, hour, minute, 0, 0);
  const brazilOffset = getBrazilTimezoneOffset(ref);
  return `${datePart}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00${brazilOffset}`;
};

/**
 * Obtém o offset do fuso horário brasileiro para uma data específica
 */
const getBrazilTimezoneOffset = (date: Date): string => {
  // Usar Intl para determinar se está em horário de verão
  const tempDate = new Date(date);
  const utc = tempDate.getTime() + (tempDate.getTimezoneOffset() * 60000);
  
  // Criar data no fuso brasileiro
  const brazilTime = new Date(utc + (-3 * 3600000)); // UTC-3 (padrão)
  
  // Verificar se é horário de verão (Brasil não usa mais desde 2019, mas mantendo flexibilidade)
  const offset = -3; // Sempre UTC-3
  
  const hours = Math.abs(offset);
  const sign = offset >= 0 ? '+' : '-';
  
  return `${sign}${hours.toString().padStart(2, '0')}:00`;
};

/**
 * Valida se uma data está dentro do range permitido para inventários
 */
export const validateInventoryDate = (dateString: string): { isValid: boolean; errorMessage?: string } => {
  if (!dateString) {
    return { isValid: false, errorMessage: 'Data é obrigatória.' };
  }
  
  const inputDate = new Date(dateString);
  const today = getBrazilNow();
  
  // Normalizar para comparar apenas as datas (não horários)
  const inputDateOnly = new Date(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate());
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  
  if (inputDateOnly > todayOnly) {
    return { isValid: false, errorMessage: 'A data do inventário não pode ser futura.' };
  }
  
  const sevenDaysAgo = new Date(todayOnly);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  
  if (inputDateOnly < sevenDaysAgo) {
    return { isValid: false, errorMessage: 'A data do inventário não pode ter mais de 7 dias de atraso.' };
  }
  
  return { isValid: true };
};

/**
 * Valida se uma data está dentro do range permitido para metas (apenas datas futuras)
 */
export const validateMetaDate = (dateString: string): { isValid: boolean; errorMessage?: string } => {
  if (!dateString) {
    return { isValid: false, errorMessage: 'Data é obrigatória.' };
  }
  
  const inputDate = new Date(dateString);
  const today = getBrazilNow();
  
  // Normalizar para comparar apenas as datas (não horários)
  const inputDateOnly = new Date(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate());
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  
  if (inputDateOnly <= todayOnly) {
    return { isValid: false, errorMessage: 'A data da meta deve ser futura.' };
  }
  
  return { isValid: true };
};

/**
 * Obtém a data máxima permitida para input[type="date"] (hoje no Brasil)
 */
export const getMaxDateForInput = (): string => {
  return toDateInputValue(getBrazilNow());
};

/**
 * Obtém a data mínima permitida para input[type="date"] (7 dias atrás no Brasil)
 */
export const getMinDateForInput = (): string => {
  const sevenDaysAgo = getBrazilNow();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  return toDateInputValue(sevenDaysAgo);
};

/**
 * Converte uma data no formato YYYY-MM-DD para UTC ISO string
 * @param dateString Data no formato YYYY-MM-DD
 * @returns ISO string em UTC ou undefined se a data for inválida
 */
export const convertDateToUTC = (dateString: string | undefined): string | undefined => {
    if (!dateString) return undefined;
    
    try {
        // Adicionar horário para evitar problemas de timezone
        const dataLocal = new Date(dateString + 'T00:00:00');
        
        // Verificar se a data é válida
        if (isNaN(dataLocal.getTime())) {
            return undefined;
        }
        
        return dataLocal.toISOString();
    } catch (error) {
        console.error('Erro ao converter data para UTC:', error);
        return undefined;
    }
};

/**
 * Converte uma data UTC ISO string para formato YYYY-MM-DD local
 * @param isoString Data em formato ISO UTC
 * @returns Data no formato YYYY-MM-DD ou empty string se inválida
 */
export const convertUTCToDateString = (isoString: string | undefined): string => {
    if (!isoString) return '';
    
    try {
        const date = new Date(isoString);
        
        // Verificar se a data é válida
        if (isNaN(date.getTime())) {
            return '';
        }
        
        // Converter para formato YYYY-MM-DD local
        return date.toISOString().split('T')[0];
    } catch (error) {
        console.error('Erro ao converter UTC para date string:', error);
        return '';
    }
}; 